import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PORTFOLIO, BEFORE_AFTER } from "../lib/siteData";
import BeforeAfterSlider from "../components/BeforeAfterSlider";

export default function PortfolioPage() {
    const [filter, setFilter] = useState("All");

    const categories = useMemo(
        () => ["All", ...Array.from(new Set(PORTFOLIO.map((p) => p.category)))],
        [],
    );

    const visible = useMemo(() => {
        if (filter === "All") return PORTFOLIO;
        return PORTFOLIO.filter((p) => p.category === filter);
    }, [filter]);

    return (
        <div data-testid="page-portfolio">
            <section className="pt-40 pb-12 max-w-7xl mx-auto px-6 md:px-10">
                <div className="eyebrow mb-6">Portfolio</div>
                <h1 className="font-display text-5xl md:text-7xl leading-[1] text-hhc-ink max-w-4xl">
                    Homes we've quietly reshaped.
                </h1>
                <p className="mt-8 text-hhc-muted text-[17px] max-w-2xl leading-relaxed">
                    A selection of interiors, renovations, and cabinetry
                    projects across Kuala Lumpur and Selangor.
                </p>
            </section>

            {/* Filter chips */}
            <section className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="flex flex-wrap gap-3 border-y border-hhc-line/70 py-6" data-testid="portfolio-filters">
                    {categories.map((c) => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setFilter(c)}
                            data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`px-5 py-2 rounded-full text-[12px] tracking-[0.18em] uppercase font-medium transition-colors ${
                                filter === c
                                    ? "bg-hhc-ink text-hhc-ivory"
                                    : "border border-hhc-line text-hhc-graphite hover:border-hhc-ink"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            {/* Grid */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-14">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
                    {visible.map((p) => (
                        <article
                            key={p.id}
                            id={p.id}
                            data-testid={`portfolio-item-${p.id}`}
                            className={`group relative overflow-hidden rounded-2xl scroll-mt-32 hover-lift ${
                                p.span || "md:col-span-6"
                            }`}
                        >
                            <div className="aspect-[4/3] md:aspect-auto md:h-full min-h-[320px]">
                                <img
                                    src={p.cover}
                                    alt={p.title}
                                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                                />
                            </div>
                            <div className="absolute inset-x-0 bottom-0 p-7 bg-gradient-to-t from-hhc-ink/85 via-hhc-ink/40 to-transparent text-hhc-ivory">
                                <div className="text-[10px] tracking-[0.28em] uppercase text-hhc-sun mb-2">
                                    {p.category}
                                </div>
                                <div className="font-display text-2xl md:text-3xl">
                                    {p.title}
                                </div>
                                <div className="text-[13px] text-hhc-cream/75 mt-1">
                                    {p.location} · {p.year}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* Before / After block */}
            <section className="bg-hhc-cream/60 border-y border-hhc-line/70 py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="mb-14">
                        <div className="eyebrow mb-4">Before · After</div>
                        <h2 className="font-display text-4xl md:text-5xl text-hhc-ink max-w-3xl">
                            Slide across. See what we mean by transformation.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {BEFORE_AFTER.map((b) => (
                            <div key={b.id} data-testid={`ba-block-${b.id}`}>
                                <BeforeAfterSlider
                                    before={b.before}
                                    after={b.after}
                                    title={b.title}
                                />
                                <p className="mt-4 text-[12px] tracking-[0.22em] uppercase text-hhc-muted">
                                    {b.title}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 text-center">
                <h3 className="font-display text-3xl md:text-4xl text-hhc-ink">
                    Ready to add your home to the list?
                </h3>
                <Link
                    to="/contact"
                    className="btn-primary text-[13px] mt-8"
                    data-testid="portfolio-cta"
                >
                    Start your project
                    <ArrowRight size={14} />
                </Link>
            </section>
        </div>
    );
}
